import { useNavigate } from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { useConfigureSso, useSpMetadata } from "../hooks";
import { parseZodErrors } from "../utils";
import SsoConfigForm from "./SsoConfigForm";

export default function SsoCreatePage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const configureSso = useConfigureSso();
  const { data: spMetadata } = useSpMetadata();

  const handleBack = () => {
    navigate({ to: "/admin/settings/sso-settings/" });
  };

  const handleSave = (values: {
    displayName: string;
    entityDescriptorUrl: string;
    emailAttribute: string;
  }) => {
    configureSso.mutate(
      {
        protocol: "SAML",
        displayName: values.displayName,
        metadataUrl: values.entityDescriptorUrl,
        emailAttribute: values.emailAttribute || undefined,
      },
      {
        onSuccess: () => {
          navigate({
            to: "/admin/settings/sso-settings/",
            search: { success: "created" },
          });
        },
      },
    );
  };

  // Parse field-specific validation errors from API response
  const fieldErrors = parseZodErrors(configureSso.error);

  // General error message (shown at top) — only for non-field errors
  const errorMessage = configureSso.error
    ? fieldErrors
      ? null // Field errors are shown inline, no top-level banner needed
      : configureSso.error instanceof Error &&
          "status" in configureSso.error &&
          (configureSso.error as { status: number }).status === 409
        ? intl.formatMessage({
            id: "lPElob",
            defaultMessage: "SSO is already configured for this tenant.",
            description:
              "Error when attempting to create SSO config that already exists",
          })
        : configureSso.error instanceof Error &&
            "message" in configureSso.error &&
            (configureSso.error.message === "Failed to fetch" ||
              configureSso.error.message === "Network Error" ||
              configureSso.error.message.toLowerCase().includes("network"))
          ? intl.formatMessage({
              id: "djfymb",
              defaultMessage:
                "Unable to reach the server. Please check your connection.",
              description:
                "Error when network request fails during SSO test connection",
            })
          : configureSso.error instanceof Error
            ? configureSso.error.message
            : intl.formatMessage({
                id: "ZzGSkd",
                defaultMessage: "Failed to configure SSO. Please try again.",
                description: "Generic error when SSO configuration fails",
              })
    : null;

  return (
    <SsoConfigForm
      title={
        <FormattedMessage
          id="VNv6vD"
          defaultMessage="Configure SAML"
          description="Configure SAML page heading"
        />
      }
      spMetadata={
        spMetadata?.saml
          ? {
              entityId: spMetadata.saml.entityId,
              acsUrl: spMetadata.saml.acsUrl,
              sloUrl: spMetadata.saml.sloUrl,
            }
          : null
      }
      isPending={configureSso.isPending}
      error={errorMessage}
      fieldErrors={fieldErrors}
      onSave={handleSave}
      onBack={handleBack}
    />
  );
}
